import { Product, queryReplaceParam, Variant, variantFirstAvailableOrDefault } from '@process-creative/slate-theme-tools';

const SEL_ELEMENT_DATA = '[data-variant-selector-data]';

type VariantSelectorContainer = HTMLInputElement | HTMLSelectElement;

export const EVT_VARIANT_CHANGED = 'onVariantChange';
export class EventVariantChanged extends CustomEvent<{
  variantSelector:VariantSelector,
  oldVariant:Variant,
  newVariant:Variant
}> {
}

export class VariantSelector {
  public readonly container:HTMLElement;
  public readonly variantSelector:VariantSelectorContainer;
  private readonly elementData:HTMLScriptElement;
  public data:Product;
  private updateHistory:boolean;

  constructor(container:HTMLElement, updateHistory:boolean=true) {
    this.container = container;
    this.updateHistory = updateHistory;
    this.variantSelector = container.querySelector('[name="id"]')!;
    this.elementData = container.querySelector<HTMLScriptElement>(SEL_ELEMENT_DATA)!;
    this.loadData(JSON.parse(this.elementData.innerHTML));

    this.variantSelector?.addEventListener('change', this.onInputChanged);
  }

  loadData(productData:Product) {
    this.elementData.innerHTML = JSON.stringify(productData);
    this.data = productData;
  }

  public getVariantId():number {
    return parseInt(this.variantSelector?.value);
  }

  public getVariant():Variant {
    const id = this.getVariantId();
    return this.data.variants.find(v => v.id === id)!;
  }

  public setVariantId(id:number) {
    if(isNaN(id) || !isFinite(id)) return;

    // when changing the variant selector after changing pseudovariant and back again this was == but despite the value needing to change.
    // commenting it out fixes this.
    // if(id.toString() == this.variantSelector.value) return;
    
    if(!this.data.variants.some(v => v.id === id)) return;

    if(!this.variantSelector) return;

    const oldVariant = this.getVariant();
    this.variantSelector.value = id.toString();
    if(this.updateHistory) {
      if(id === variantFirstAvailableOrDefault({ variants: this.data.variants }).id) {
        queryReplaceParam('variant', null);
      } else {
        queryReplaceParam('variant', id.toString());
      }
    }
    this.onVariantSelected();
    const newVariant = this.getVariant();
    const evt = new EventVariantChanged(EVT_VARIANT_CHANGED, {
      bubbles: true,
      cancelable: false,
      detail: {
        oldVariant, newVariant, variantSelector: this
      }
    });
    this.variantSelector.dispatchEvent(evt);
  }

  public getOptionValues() {
    const values:{ [key:string]:string[] } = {};

    this.data.options.forEach(option => values[option] = []);

    this.data.variants.forEach(variant => {
      variant.options.forEach((opt,i) => {
        const key = this.data.options[i];
        if(values[key].indexOf(opt) !== -1) return;
        values[key].push(opt);
      });
    });

    return values;
  }

  public setVariantOption(index:number, value:string) {;
    const options = this.getOptionValues();
    const currentVariant = this.getVariant();

    const intended:string[] = [];

    for(let i = 0; i < this.data.options.length; i++) {
      let selection:string;
      if(i === index) {
        selection = value;
      } else {
        selection = currentVariant.options[i];
      }
      intended.push(selection);
    }

    // Try find variant
    const newVariant = this.data.variants.find(v => {
      return v.options.every((o,oi) => {
        if(oi >= this.data.options.length) {
          return true;
        } 
        return intended[oi] === o;
      });
    });

    if(!newVariant) return null;

    this.setVariantId(newVariant.id);
    return newVariant;
  }

  private onInputChanged = (e:Event) => {
    this.setVariantId(parseInt(this.variantSelector.value));
  }

  public onVariantSelected() {}

  public dispose() {
    this.variantSelector.removeEventListener('change', this.onInputChanged);
  }
}